import React, { useCallback, useState } from 'react';
import * as Yup from 'yup';
import { CreateReport } from '../../../components';
import { paths } from '../../../routes';

export const PRMAssistanceForms: React.FC = () => {
  const [bookingNumber, setBookingNumber] = useState('');
  const [prmAssistance, setPrmAssistance] = useState<
    'Prise en charge' | 'Refus de prise en charge' | undefined
  >(undefined);

  const handleOnChange = useCallback(
    (form) => {
      const prmAssistanceFields = form?.values?.fieldReportsDto;
      const bookingNumberValue = prmAssistanceFields?.customerInfo?.bookingNumber ?? '';
      const prmAssistanceValue = prmAssistanceFields?.assistanceNature;
      const { setFieldValue } = form;

      if (bookingNumberValue !== bookingNumber) {
        const newValue = bookingNumberValue.replace(/[^a-zA-Z0-9]/ig, '').toUpperCase();
        setBookingNumber(() => {
          setFieldValue('fieldReportsDto.customerInfo.bookingNumber', newValue);
          return newValue;
        });
      }

      if (prmAssistanceValue !== prmAssistance) {
        setPrmAssistance(() => {
          if (prmAssistanceValue === 'Refus de prise en charge') {
            setFieldValue('fieldReportsDto.assistanceSupport', '');
            setFieldValue('fieldReportsDto.assistanceNeed', '');
            return prmAssistanceValue;
          }
        });
      }
    },
    [bookingNumber, prmAssistance]
  );
  return (
    <CreateReport
      initialValues={{
        fieldReportsDto: {},
      }}
      themeShortCode={paths.prmAssistance.themeShortCode || ''}
      otherFields={{
        section1: {
          type: 'section',
          name: 'section1',
          label: "Veuillez indiquer l'identité du client ainsi que son numéro de DV",
        },
        'fieldReportsDto.customerInfo.customerId': {
          type: 'text',
          name: 'customerId',
          label: 'Identité du client',
          grid: 6,
        },
        'fieldReportsDto.customerInfo.bookingNumber': {
          type: 'text',
          name: 'bookingNumber',
          label: 'Numéro de DV du client',
          grid: 6,
          inputProps: {
            maxLength: 6,
          },
        },
        'fieldReportsDto.assistanceNature': {
          type: 'radio',
          name: 'assistanceNature',
          label: "Nature de l'assistance PMR",
          options: [
            {
              value: 'Prise en charge',
              label: 'Prise en charge',
            },
            {
              value: 'Refus de prise en charge',
              label: 'Refus de prise en charge',
            },
          ],
          grid: 12,
        },
        'fieldReportsDto.refusalReasonComment': {
          type: 'text',
          label: 'Détailler les raisons du refus de la prise en charge',
          multiline: true,
          rows: 4,
          rowsMax: 15,
          disabled: prmAssistance !== 'Refus de prise en charge',
          helperText: (
            <i>
              *Exemples : - Autres missions prioritaires en cours (sécurité, sûreté, évacuation
              d’une personne malade, etc.)
              <br />
              - 1 prestation PSH déjà programmée sur la gare
              <br />
              - Client ne peut porter seul ses bagages
              <br />
              - Titre de transport non valable / pas de titre
              <br />- Client en fauteuil roulant / Assistance non éligible (UFR) - Incompatibilité
              matériel roulant/gare (ex. aveugle)`,
            </i>
          ),
        },
        'fieldReportsDto.assistanceSupport': {
          type: 'radio',
          name: 'assistanceSupport',
          label: 'Veuillez indiquer le type de prise en charge effectuée',
          options: [
            {
              value: 'Aide à la montée',
              label: 'Aide à la montée',
            },
            {
              value: 'Aide à la descente',
              label: 'Aide à la descente',
            },
            {
              value: 'Aide à la montée + Aide à la descente',
              label: 'Aide à la montée + Aide à la descente',
            },
          ],
          grid: 4,
          disabled: prmAssistance === 'Refus de prise en charge',
        },
        'fieldReportsDto.assistanceNeed': {
          type: 'radio',
          name: 'assistanceNeed',
          label: 'Veuillez indiquer la nature du "besoin d\'assistance"',
          options: [
            {
              value: 'Locomotion restreinte',
              label: 'Locomotion restreinte',
            },
            {
              value: 'Gêne cognitive (déficients mentaux)',
              label: 'Gêne cognitive (déficients mentaux)',
            },
            {
              value: 'Langue des signes/malvoyant',
              label: 'Langue des signes/malvoyant',
            },
            {
              value: 'Personne enceinte',
              label: 'Personne enceinte',
            },
            {
              value: 'Sénior',
              label: 'Sénior',
            },
          ],
          grid: 4,
          disabled: prmAssistance === 'Refus de prise en charge',
        },
        'fieldReportsDto.disembarkationHelp': {
          type: 'radio',
          name: 'disembarkationHelp',
          label: 'Si aide à la descente, le client sera assisté par',
          options: [
            {
              value: 'Bord',
              label: 'Bord',
            },
            {
              value: 'Sol',
              label: 'Sol',
            },
            {
              value: 'Gare encadrante Bord',
              label: 'Gare encadrante Bord',
            },
            {
              value: 'Gare encadrante Sol',
              label: 'Gare encadrante Sol',
            },
            {
              value: 'Par ses propres moyens (seul)',
              label: 'Par ses propres moyens (seul)',
            },
            {
              value: 'Par ses propres moyens (accompagné)',
              label: 'Par ses propres moyens (accompagné)',
            },
            {
              value: 'Non concerné',
              label: 'Non concerné',
            },
          ],
          grid: 4,
        },
        'fieldReportsDto.comment': {
          type: 'text',
          label: 'Commentaire libre',
          multiline: true,
          rows: 4,
          rowsMax: 15,
        },
      }}
      onChange={handleOnChange}
      validationSchema={Yup.object({
        serviceName: Yup.string().required('Ce champ est requis').nullable(),
        fieldReportsDto: Yup.object({
          customerInfo: Yup.object({
            customerId: Yup.string().required('Ce champ est requis'),
          }),
          assistanceNature: Yup.string().required('Ce champ est requis'),
          assistanceSupport: Yup.string().when('assistanceNature', {
            is: 'Prise en charge',
            then: Yup.string().required('Ce champ est requis'),
          }),
          assistanceNeed: Yup.string().when('assistanceNature', {
            is: 'Prise en charge',
            then: Yup.string().required('Ce champ est requis'),
          }),
          disembarkationHelp: Yup.string().required('Ce champ est requis'),
        }),
      })}
    />
  );
};

export default PRMAssistanceForms;
